import { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query";

import { orderLayersByConfiguration, useGetVersionConfigQuery } from "fond/api";
import { useGetImageOverlaysQuery, useGetOverlaysQuery } from "fond/api/overlaysSlice";
import { MAX_OVERLAY_COUNT } from "fond/constants";
import { Configuration, GroupConfig, MultiProject, Project } from "fond/types";
import { CustomOverlayItem, ImageOverlayItem } from "fond/types/overlays";
import { LayerConfig, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { isMultiProject } from "fond/utils/project";

const EMPTY_OVERLAY_METADATA: Record<string, OverlayMetadata> = {};
const EMPTY_IMAGE_OVERLAYS: ImageOverlayItem[] = [];
const OVERLAYS_PARENT_GROUP_ID = "overlays-parent-group";
const IMAGES_PARENT_GROUP_ID = "images-parent-group";

// Poll image overlays to keep pre-signed ImageUrls fresh before they expire.
const IMAGE_OVERLAY_POLL_INTERVAL = 3_600_000; // 1 hour
/**
 * Combined data structure for an overlay with its configuration
 */
export interface OverlayWithConfig {
  overlay: CustomOverlayItem;
  config: Configuration;
}

/**
 * Metadata for a single overlay, keyed by overlay group ID
 */
export interface OverlayMetadata {
  /** The overlay's unique ID */
  overlayId: string;
  /** The source version ID for this overlay */
  sourceVersionId: string;
  /** The source project ID for this overlay */
  sourceProjectId: string;
}

/**
 * Result type for the useOverlaysWithConfigs hook
 */
export interface UseOverlaysWithConfigsResult {
  overlays: Configuration | null;
  /** Flat array of overlay layer configs, extracted from the merged overlay configuration */
  overlayLayerConfigs: Array<LayerConfig | SublayerConfig>;
  /** Map of overlay group ID to overlay metadata */
  overlayMetadata: Record<string, OverlayMetadata>;
  /** Image overlays for the current project */
  imageOverlays: ImageOverlayItem[];
  isLoading: boolean;
  error: unknown;
}

/**
 * Merges multiple overlay configurations into a single Configuration.
 * Each overlay is wrapped in a GROUP node.
 * Returns the merged configuration and metadata for each overlay.
 */
const mergeOverlayConfigs = (
  overlaysWithConfigs: OverlayWithConfig[]
): {
  config: Configuration;
  overlayMetadata: Record<string, OverlayMetadata>;
} | null => {
  if (overlaysWithConfigs.length === 0) {
    return null;
  }

  const mergedEntities: Configuration["Data"]["entities"] = {};
  const mergedIds: string[] = [];
  const overlayGroupIds: string[] = [];
  const overlayMetadata: Record<string, OverlayMetadata> = {};

  overlaysWithConfigs.forEach(({ overlay, config }, index) => {
    const overlayGroupId = `overlay-group-${overlay.ID}`;

    // Store the metadata for this overlay
    overlayMetadata[overlayGroupId] = {
      overlayId: overlay.ID,
      sourceVersionId: overlay.SourceVersion.ID,
      sourceProjectId: overlay.SourceVersion.Project.ID,
    };

    // Copy all entities from this overlay's config
    Object.entries(config.Data.entities).forEach(([id, entity]) => {
      if (entity) {
        // Update ParentID for children that should be nested under this overlay's GROUP
        if (config.MapChildren.includes(id)) {
          mergedEntities[id] = {
            ...entity,
            ParentID: overlayGroupId,
          } as typeof entity;
        } else {
          mergedEntities[id] = entity;
        }
        mergedIds.push(id);
      }
    });

    // Create a GROUP wrapper for this overlay
    // All overlay groups will be children of the parent "Overlays" group
    const overlayGroup: GroupConfig = {
      ID: overlayGroupId,
      Type: "GROUP",
      Key: `overlay-${overlay.ID}`,
      Label: overlay.Name,
      IsVisible: true,
      Hidden: false,
      Position: index,
      GlobalPosition: index,
      ParentID: OVERLAYS_PARENT_GROUP_ID,
      RootID: null,
      Children: config.MapChildren,
    };

    mergedEntities[overlayGroupId] = overlayGroup;
    mergedIds.push(overlayGroupId);
    overlayGroupIds.push(overlayGroupId);
  });

  // Create the parent "Overlays" GROUP that contains all overlay groups
  const parentOverlaysGroup: GroupConfig = {
    ID: OVERLAYS_PARENT_GROUP_ID,
    Type: "GROUP",
    Key: "overlays-parent",
    Label: "Overlays",
    IsVisible: true,
    Hidden: false,
    Position: 0,
    GlobalPosition: 0,
    ParentID: null,
    RootID: null,
    Children: overlayGroupIds,
  };

  mergedEntities[OVERLAYS_PARENT_GROUP_ID] = parentOverlaysGroup;
  mergedIds.push(OVERLAYS_PARENT_GROUP_ID);

  // Create the merged configuration
  return {
    config: {
      ID: "overlays-merged-root",
      Key: "overlays-merged",
      SourceID: "overlays",
      Type: "MapLayerConfig",
      MapChildren: [OVERLAYS_PARENT_GROUP_ID],
      Data: {
        ids: mergedIds,
        entities: mergedEntities,
      },
    },
    overlayMetadata,
  };
};

/**
 * Injects image overlay entries into the merged overlay Configuration.
 * Adds an "Images" GROUP under the root "Overlays" parent, with a
 * LAYER entry for each image so they get layer styling in the tree.
 */
const injectImageOverlays = (
  mergedResult: { config: Configuration; overlayMetadata: Record<string, OverlayMetadata> } | null,
  imageOverlayItems: ImageOverlayItem[]
): { config: Configuration; overlayMetadata: Record<string, OverlayMetadata> } | null => {
  if (imageOverlayItems.length === 0) return mergedResult;

  const existingEntities = mergedResult?.config.Data.entities ?? {};
  const existingIds = mergedResult?.config.Data.ids ?? [];
  const existingParent = existingEntities[OVERLAYS_PARENT_GROUP_ID] as GroupConfig | undefined;
  const existingOverlayGroupIds = existingParent?.Children ?? [];

  const newEntities: Configuration["Data"]["entities"] = { ...existingEntities };
  const newIds = [...existingIds];
  const imageGroupIds: string[] = [];

  // Create a LAYER entry for each image overlay so it gets layer styling in the tree
  imageOverlayItems.forEach((image, index) => {
    const layerId = `image-overlay-${image.ID}`;
    const layer: LayerConfig = {
      ID: layerId,
      Type: "LAYER",
      Key: layerId,
      Label: image.Name,
      IsVisible: true,
      Hidden: false,
      Position: index,
      GlobalPosition: index,
      GeometryType: "Polygon",
      ParentID: IMAGES_PARENT_GROUP_ID,
      MaxZoomLevel: null,
      MinZoomLevel: null,
      Children: [],
      Styles: [],
    };
    newEntities[layerId] = layer;
    newIds.push(layerId);
    imageGroupIds.push(layerId);
  });

  // Create the "Images" parent group
  const imagesParent: GroupConfig = {
    ID: IMAGES_PARENT_GROUP_ID,
    Type: "GROUP",
    Key: "image-overlay-parent",
    Label: "Images",
    IsVisible: true,
    Hidden: false,
    Position: existingOverlayGroupIds.length,
    GlobalPosition: existingOverlayGroupIds.length,
    ParentID: OVERLAYS_PARENT_GROUP_ID,
    RootID: null,
    Children: imageGroupIds,
  };
  newEntities[IMAGES_PARENT_GROUP_ID] = imagesParent;
  newIds.push(IMAGES_PARENT_GROUP_ID);

  // Update or create the root "Overlays" parent group
  const parentChildren = [...existingOverlayGroupIds, IMAGES_PARENT_GROUP_ID];
  const parentGroup: GroupConfig = {
    ID: OVERLAYS_PARENT_GROUP_ID,
    Type: "GROUP",
    Key: "overlays-parent",
    Label: "Overlays",
    IsVisible: true,
    Hidden: false,
    Position: 0,
    GlobalPosition: 0,
    ParentID: null,
    RootID: null,
    Children: parentChildren,
  };
  newEntities[OVERLAYS_PARENT_GROUP_ID] = parentGroup;
  if (!existingIds.includes(OVERLAYS_PARENT_GROUP_ID)) {
    newIds.push(OVERLAYS_PARENT_GROUP_ID);
  }

  return {
    config: {
      ID: "overlays-merged-root",
      Key: "overlays-merged",
      SourceID: "overlays",
      Type: "MapLayerConfig",
      MapChildren: [OVERLAYS_PARENT_GROUP_ID],
      Data: {
        ids: newIds,
        entities: newEntities,
      },
    },
    overlayMetadata: mergedResult?.overlayMetadata ?? {},
  };
};

/**
 * Merges a project configuration with an overlay configuration into a single unified Configuration.
 * This is useful when consumers need to treat project layers and overlay layers as one combined set
 * (e.g. for feature selection popups that need to identify features from both sources).
 *
 * @param config - The base project configuration
 * @param overlayConfig - The merged overlay configuration (from useOverlaysWithConfigs), or null if no overlays
 * @returns A single Configuration containing both project and overlay entities
 */
export const mergeConfigWithOverlays = (config: Configuration, overlayConfig: Configuration | null): Configuration => {
  if (!overlayConfig) return config;
  return {
    ...config,
    Data: {
      ids: [...config.Data.ids, ...overlayConfig.Data.ids],
      entities: { ...config.Data.entities, ...overlayConfig.Data.entities },
    },
    MapChildren: [...config.MapChildren, ...overlayConfig.MapChildren],
  };
};

/**
 * Hook that loads overlays for a project/version and their associated root configurations.
 *
 * This hook:
 * 1. Fetches the list of custom overlays for the given version or multi-project
 * 2. For each overlay, fetches the root configuration of its source version
 * 3. Waits for ALL configurations to load before returning data (Promise.all behavior)
 * 4. Merges all overlay configs into a single Configuration with a GROUP for each overlay
 * 5. Injects image overlays as an "Images" GROUP in the tree
 * 6. Returns null while loading, merged Configuration once all data is ready
 *
 * @param versionId - The version ID to load overlays for
 * @param project - The current project (used to determine if multi-project)
 * @returns Merged configuration containing all overlays. `overlays` is null until all configs load.
 */
export const useOverlaysWithConfigs = (versionId: string | undefined, project: Project | MultiProject | undefined): UseOverlaysWithConfigsResult => {
  const multiProjectId = project && isMultiProject(project) ? project.ID : undefined;
  const {
    data: overlaysData,
    isLoading: isLoadingOverlays,
    error: overlaysError,
  } = useGetOverlaysQuery(multiProjectId ? { multiProjectId } : versionId ? { versionId } : skipToken, {
    skip: !versionId && !multiProjectId,
  });

  // Fetch image overlays
  const { data: imageOverlayData } = useGetImageOverlaysQuery(multiProjectId ? { multiProjectId } : versionId ? { versionId } : skipToken, {
    skip: !versionId && !multiProjectId,
    pollingInterval: IMAGE_OVERLAY_POLL_INTERVAL,
  });
  const imageOverlayItems = imageOverlayData?.ImageOverlays ?? EMPTY_IMAGE_OVERLAYS;

  // Extract the overlay items and memoize to avoid recreating on every render
  const overlayItems = useMemo(() => overlaysData?.CustomOverlays ?? [], [overlaysData]);

  // Load root configuration for each overlay's source version
  // IMPORTANT: We use a fixed-size array to satisfy React's rules of hooks.
  // If we mapped directly over overlayItems, adding/removing overlays would change
  // the number of hook calls between renders, causing React to error.
  // Queries beyond the actual overlay count are skipped via the skip parameter (no network cost).
  const configQueries = Array.from({ length: MAX_OVERLAY_COUNT }, (_, index) => {
    const overlay = overlayItems[index];
    const sourceVersionId = overlay?.SourceVersion?.ID;

    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useGetVersionConfigQuery(sourceVersionId ?? skipToken, {
      skip: !sourceVersionId,
    });
  });

  // Aggregate loading and error states across all queries
  const activeQueries = configQueries.slice(0, overlayItems.length);
  const isLoadingConfigs = activeQueries.some((query) => query.isLoading);
  const configErrors = activeQueries.map((query) => query.error).filter(Boolean);
  const allConfigsLoaded = activeQueries.length > 0 && activeQueries.every((query) => query.data !== undefined || query.error);

  // Overall loading state - wait for overlays AND all configs
  const isLoading = isLoadingOverlays || isLoadingConfigs;

  // Extract stable references to config data for useMemo dependencies.
  // configQueries is a new array each render, but RTK Query's .data references are stable.
  const configDatas = configQueries.map((query) => query.data);

  // Build array of overlay configs, then merge into single Configuration
  const overlaysWithConfigs: OverlayWithConfig[] = useMemo(() => {
    if (isLoading || !allConfigsLoaded) {
      return [];
    }

    return overlayItems
      .map((overlay, index) => {
        const config = configDatas[index];
        // Filter out overlays where config failed to load
        if (!config) {
          return null;
        }
        // Remove projectComments from MapChildren
        return {
          overlay,
          config: {
            ...config,
            MapChildren: config.MapChildren.filter((id) => id !== "projectComments"),
          },
        };
      })
      .filter((item): item is OverlayWithConfig => item !== null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, allConfigsLoaded, overlayItems, ...configDatas]);

  // Merge all overlay configs into a single Configuration
  const mergedResult = useMemo(() => mergeOverlayConfigs(overlaysWithConfigs), [overlaysWithConfigs]);

  // Inject image overlays into the configuration tree
  const finalResult = useMemo(() => injectImageOverlays(mergedResult, imageOverlayItems), [mergedResult, imageOverlayItems]);

  const overlayConfig = finalResult?.config ?? null;

  // Extract a flat array of overlay layer configs for consumers that need to match features
  const overlayLayerConfigs = useMemo(() => (overlayConfig ? orderLayersByConfiguration(overlayConfig) : []), [overlayConfig]);

  return {
    overlays: overlayConfig,
    overlayLayerConfigs,
    overlayMetadata: finalResult?.overlayMetadata ?? EMPTY_OVERLAY_METADATA,
    imageOverlays: imageOverlayItems,
    isLoading,
    error: overlaysError || (configErrors.length > 0 ? configErrors[0] : undefined),
  };
};
